/**
 * The readings behind Macro › The releases, grouped (`docs/plans/macro.md`).
 *
 * One read: qv-data's admin-only `GET /macro/series?ids=&since=`
 * (`services/qv-data/app/routers/macro_series.py`), which serves the macro
 * store's series as stored from the `macro_series` artifact. The mapping into
 * display units — the YoYs, thousands → M, the target range, the real yield —
 * is `lib/macro-release-series.ts`. Pending tiles (ISM, the Conference Board) ask for nothing and
 * stay `pending`.
 *
 * Reads degrade, `api/admin.ts`'s rule: a failure is an empty payload with a
 * `note`, and the tiles say they have no readings rather than erroring. The
 * route answers **503 until the artifact has been built and pulled** (the
 * `macro-series-derive` job is not scheduled yet), which is the note's commonest
 * cause.
 */

import { requestHeaders } from "../lib/user";
import type { ReleaseData } from "../lib/macro-releases";
import {
  RELEASE_SERIES_IDS,
  releaseSince,
  releasesFromSeries,
  type MacroSeriesResponse,
} from "../lib/macro-release-series";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

export interface MacroReleasesPayload {
  series: ReleaseData;
  /** Why there is nothing, when there is nothing to show. */
  note?: string;
}

/** The section's one read. Never throws. */
export async function fetchMacroReleases(today = new Date().toISOString().slice(0, 10)): Promise<MacroReleasesPayload> {
  if (!BASE) return { series: {}, note: "No data server configured" };
  const query = `ids=${encodeURIComponent(RELEASE_SERIES_IDS.join(","))}&since=${releaseSince(today)}`;
  try {
    const response = await fetch(`${BASE}/macro/series?${query}`, { headers: await requestHeaders() });
    if (response.status === 503) return { series: {}, note: "Macro series not built on this server yet" };
    if (!response.ok) return { series: {}, note: `Macro series unavailable (${response.status})` };
    return { series: releasesFromSeries((await response.json()) as MacroSeriesResponse) };
  } catch {
    return { series: {}, note: "Macro series unavailable" };
  }
}
